// @flow

import * as React from 'react';
import WindowResize from '@ta/events.window-resize';

type Props = {|
  /** The function to call when we realized we've collapsed. Could happen on mount or window resize. */
  collapsedCallback: () => void,

  /** The function to call when we realized we're no longer collapsed. Happens on window resize. */
  uncollapsedCallback: () => void,

  /** Contents of the list item */
  children: React.Node,
|};

type State = {|
  /** Our most recent understanding of the collapsed state */
  isCollapsed: boolean,
|};

/**
 * A list item that if collapsed calls a callback, e.g. so that it could go in a dropdown of collapsed items.
 * Requires that the parent forces its children (such as this component) to wrap and be hidden on overflow.
 */
class CollapsibleListItem extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.listItemRef = React.createRef();
    this.state = { isCollapsed: false };
  }

  componentDidMount() {
    this.checkIfOverflowed();
  }

  componentWillUnmount() {
    this.props.uncollapsedCallback();
  }

  checkIfOverflowed = () => {
    if (!this.listItemRef.current) {
      return;
    }

    const top = this.listItemRef.current.offsetTop;
    if (typeof top !== 'undefined') {
      if (top > 0 && !this.state.isCollapsed) {
        // We've just realized we've collapsed, let's call the collapsedCallback.
        // Let's only do it once state is updated by using setState's callback to avoid calling it multiple times, i.e. if WindowResize fires faster than state updates.
        this.setState({ isCollapsed: true }, this.props.collapsedCallback);
      } else if (top === 0 && this.state.isCollapsed) {
        // It's not collapsed anymore, let's call the uncollapsedCallback.
        this.setState({ isCollapsed: false }, this.props.uncollapsedCallback);
      }
    }
  };

  listItemRef: any;

  render() {
    return (
      <React.Fragment>
        <li ref={this.listItemRef}>{this.props.children}</li>
        <WindowResize callback={this.checkIfOverflowed} />
      </React.Fragment>
    );
  }
}

export default CollapsibleListItem;
